import { FC, useEffect, useMemo, useRef, useState } from "react";
import HorizontalPadding from "../../components/HorizontalPadding";
import { formatName, validateAccount } from "../../utils";
import {
  AccountInterface,
  BusinessType,
  BusinessUnitInterface,
  DeliveryType,
  PaymentMode,
  PieceInterface,
  ShipmentInterface,
  ShipmentService,
  ShipmentStatus,
} from "../../interfaces";
import {
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import classNames from "classnames";
import { ClientDetails } from "../../components/Shipment/ShipmentHeader";
import { useAppSelector } from "../../store/hooks";
import { AccountSearch } from "../../components/Account/AccountSearch";
import UpdateClientModal from "../../components/Account/UpdateClientModal";
import CreateClientModal from "../../components/Account/CreateClientModal";
import { BusinessUnitOption } from "../../components/LocationSearch";
import ShipmentPiecesDetails from "../Shipments/ShipmentPiecesDetails";
import { ServiceDTO } from "../../interfaces/Dtos/ServiceDTO";
import {
  alertService,
  getAllDeclaredShipmentDocuments,
  getBusinessUnits,
  getService,
  getWeights,
  loaderService,
  PrintShipmentLabel,
  saveShipment,
} from "../../services";
import {
  FormSearch,
  FormTextAreaInput,
  FormTextInput,
} from "../../components/FormFields";
import {
  LinkText,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons";
import { ShipmentItem } from "../Shipments/ShipmentResume";
import { useNavigate } from "react-router-dom";
import { OriginModal } from "../../components/Shipment/OriginModal";
import { saveLiabilityWaver } from "../../services/liabilityWaiverServices";

enum Phase {
  FORM,
  RESUME,
}

// A destinatario committed to the lote: one EXEMPT guía sharing the top-level
// shipper and the single declaración jurada. Snapshotted from the form on
// "Añadir destinatario" so the proven single-guía form stays untouched.
interface AddedDestination {
  consignee: AccountInterface;
  buConsignee: BusinessUnitInterface;
  reference: string;
  pieces: PieceInterface[];
  observations: string;
  physicalWeight: number;
  dimensionalWeight: number;
  chargedWeight: number;
}

const InternalMail: FC = () => {
  const navigate = useNavigate();
  const loading = loaderService.useIsLoading();
  const user = useAppSelector((state) => state.user);
  const buSource = useAppSelector((state) => state.user.businessUnit);
  const countries = useAppSelector((state) => state.inmutable.countries);
  const pieceCategories = useAppSelector(
    (state) => state.inmutable.pieceCategories
  );
  const applicationID = useAppSelector(
    (state) => state.inmutable.appData.applicationID
  );

  const [phase, setPhase] = useState(Phase.FORM);
  const [cleaned, setCleaned] = useState(false);
  const [shipper, setShipper] = useState<AccountInterface>();
  const [consignee, setConsignee] = useState<AccountInterface>();
  const [buConsignee, setBUConsignee] = useState<BusinessUnitInterface>();
  const [reference, setReference] = useState("");
  const [service, setService] = useState<ServiceDTO>();
  const [pieces, setPieces] = useState<PieceInterface[]>([]);
  const [physicalWeight, setPhysicalWeight] = useState(0);
  const [dimensionalWeight, setDimensionalWeight] = useState(0);
  const [chargedWeight, setChargedlWeight] = useState(0);
  const [observations, setObservations] = useState("");
  // Destinatarios committed to the lote (form snapshots), and the guías saved
  // for them once the agent finalizes.
  const [drafts, setDrafts] = useState<AddedDestination[]>([]);
  const [shipments, setShipments] = useState<ShipmentInterface[]>([]);
  const [businessUnitSearch, setBusinessUnitSearch] = useState("");
  const [businessUnits, setBusinessUnits] = useState<BusinessUnitInterface[]>(
    []
  );

  const [liabilityId, setLiabilityId] = useState<string>();
  const [liabilityName, setLiabilityName] = useState<string>();
  const [isPhysicalLiability, setIsPhysicalLiability] = useState(false);
  const [liabilityFingerprint, setLiabilityFingerprint] = useState<string>();
  const [liabilityObservation, setLiabilityObservation] = useState<string>();
  const [liabilityAttachments, setLiabilityAttachments] = useState<string[]>(
    []
  );

  const [openShipperEditModal, setOpenShipperEditModal] = useState(false);
  const [openConsigneeEditModal, setOpenConsigneeEditModal] = useState(false);
  const [openShipperCreateModal, setOpenShipperCreateModal] = useState(false);
  const [openOriginWaiverModal, setOpenOriginWaiverModal] = useState(false);
  const [openConsigneeCreateModal, setOpenConsigneeCreateModal] =
    useState(false);

  // H1: protect the legal-coverage guarantee. While the resume screen holds
  // saved guías with no persisted declaración jurada, warn on tab close/refresh
  // and log if the screen is abandoned, so orphaned guías don't slip by.
  const waiverSavedRef = useRef(false);
  const shipmentsRef = useRef(shipments);
  shipmentsRef.current = shipments;

  // Idempotency latch for the declaración jurada. ApiCore inserts one
  // LiabilityWaiver row per guía with NO existence check, so a second POST
  // creates a SECOND waiver for every guía in the lote — and the PDF's
  // attachment query is keyed on LiabilityWaiverID, so the fingerprint and
  // every photo then render twice. A ref (not state) because the guard has to
  // hold within a single tick, before React re-renders.
  const savingWaiverRef = useRef(false);

  useEffect(() => {
    const warnOnUnload = (event: BeforeUnloadEvent) => {
      if (shipmentsRef.current.length > 0 && !waiverSavedRef.current) {
        event.preventDefault();
        // Non-empty string: some browsers ignore "" and skip the prompt.
        event.returnValue =
          "Hay guías guardadas sin declaración jurada. ¿Salir de todos modos?";
      }
    };
    window.addEventListener("beforeunload", warnOnUnload);
    return () => {
      window.removeEventListener("beforeunload", warnOnUnload);
      if (shipmentsRef.current.length > 0 && !waiverSavedRef.current) {
        console.warn(
          "[declaracion-jurada] Correo Interno cerrado con guías guardadas sin declaración jurada:",
          shipmentsRef.current.map((s) => s.shipmentNumber).join(", ")
        );
      }
    };
  }, []);

  const isShipperEnterprise = useMemo(() => {
    return (
      !!shipper?.agreementID ||
      (shipper?.listAuthorizingAccount?.length ?? 0) > 0
    );
  }, [shipper]);

  const isConsigneeEnterprise = useMemo(() => {
    return (
      !!consignee?.agreementID ||
      (consignee?.listAuthorizingAccount?.length ?? 0) > 0
    );
  }, [consignee]);

  const businessUnitOptions = useMemo(() => {
    const buSourceId = buSource?.id ?? user.businessUnit?.id;

    const business = businessUnits.filter(
      (businessUnit) => businessUnit.id !== buSourceId && !businessUnit.isLocked
    );

    // Delete duplicates by id
    const uniqueIds = new Set();
    const result: BusinessUnitInterface[] = [];
    business.forEach((businessUnit, index) => {
      if (!uniqueIds.has(businessUnit.id)) {
        uniqueIds.add(businessUnit.id);
        result.push(businessUnit);
      }
    });

    result.sort((a, b) => {
      const tokens = businessUnitSearch.split(" ");

      const aMatch = tokens.some((token) => token === a.code);
      const bMatch = tokens.some((token) => token === b.code);

      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      return 0;
    });

    return result;
  }, [businessUnits, user.businessUnit, businessUnitSearch, buSource?.id]);

  const statusMessage = useMemo(() => {
    if (!liabilityFingerprint && !isPhysicalLiability) {
      return "La guía NO tiene cargada la huella dactilar NI está marcada como registro físico para la Declaración Jurada.";
    } else if (liabilityFingerprint) {
      return "La guía tiene cargada la huella dactilar para la Declaración Jurada.";
    } else if (isPhysicalLiability) {
      return "La guía está marcada como registro físico para la Declaración Jurada.";
    }

    return "";
  }, [liabilityFingerprint, isPhysicalLiability]);

  const dataMessage = useMemo(() => {
    const warningMessage =
      "(No se podrá continuar sin estos datos para la Declaración Jurada).";

    if (!liabilityName && !liabilityId) {
      return `El nombre y cédula de identidad del remitente es requerido ${warningMessage}.`;
    } else if (!liabilityName) {
      return `El nombre del remitente es requerido ${warningMessage}.`;
    } else if (!liabilityId) {
      return `La cédula de identidad del remitente es requerido ${warningMessage}.`;
    }

    return "";
  }, [liabilityName, liabilityId]);

  const alertMessage = useMemo(() => {
    if (isPhysicalLiability) {
      return "Recuerde que debe registrar la huella y firma en el documento físico.";
    }

    return "";
  }, [isPhysicalLiability]);

  const handleShipperChange = async (shipper: AccountInterface) => {
    const validations = await validateAccount(shipper, countries);

    setShipper(shipper);
    setBUConsignee(undefined);

    if (shipper && validations.error) {
      setOpenShipperEditModal(true);
    }
  };

  const handleConsigneeChange = async (consignee: AccountInterface) => {
    const validations = await validateAccount(consignee, countries);

    setConsignee(consignee);
    setBUConsignee(undefined);

    if (shipper && validations.error) {
      setOpenConsigneeEditModal(true);
    }
  };

  // Reset only the per-destinatario fields (keep the shared shipper + the
  // already-added destinatarios).
  const resetDestinationForm = () => {
    setConsignee(undefined);
    setBUConsignee(undefined);
    setReference("");
    setPieces([]);
    setObservations("");
    setBusinessUnitSearch("");
    setCleaned(true);
  };

  const onClean = () => {
    setShipper(undefined);
    setDrafts([]);
    setShipments([]);
    resetDestinationForm();
  };

  // Validate the current form as one destinatario. Returns the snapshot or null.
  const buildCurrentDestination = (): AddedDestination | null => {
    const minWeight = service?.minWeight ?? 0;

    if (!shipper) {
      alertService.error("El remitente es obligatorio.");
      return null;
    }
    if (!consignee) {
      alertService.error("El destinatario es obligatorio.");
      return null;
    }
    if (!buConsignee) {
      alertService.error("La tienda destino es obligatoria.");
      return null;
    }
    if (pieces.length === 0) {
      alertService.error("Debe haber al menos una pieza.");
      return null;
    }
    if (chargedWeight < minWeight) {
      alertService.error(
        `El peso a facturar debe ser mayor a ${minWeight.toFixed(2)} Kg.`
      );
      return null;
    }

    return {
      consignee,
      buConsignee,
      reference,
      pieces,
      observations,
      physicalWeight,
      dimensionalWeight,
      chargedWeight,
    };
  };

  // "Añadir destinatario": commit the current form to the lote and clear it for
  // the next destinatario.
  const addDestination = () => {
    const destination = buildCurrentDestination();
    if (!destination) return;

    setDrafts((prev) => [...prev, destination]);
    resetDestinationForm();
  };

  const removeDestination = (index: number) => {
    setDrafts((prev) => prev.filter((_, i) => i !== index));
  };

  const buildShipment = (destination: AddedDestination): ShipmentInterface => ({
    shipper: shipper!,
    consignee: destination.consignee,
    accountBillTo: shipper,
    buSource: buSource!,
    deliveryType: DeliveryType.AT_HOME,
    consigneeAddress: {
      ...destination.buConsignee.location,
      reference: destination.reference,
    },
    buConsignee: destination.buConsignee,
    pieces: destination.pieces,
    items: [],
    paymentMode: PaymentMode.EXEMPT,
    observations: destination.observations,
    service: ShipmentService.INTERNAL_MAIL,
    status: ShipmentStatus.ACTIVE,
    total: 0,
    totalChargedWeight: destination.chargedWeight,
    totalPhysicalWeight: destination.physicalWeight,
    totalDimensionalWeight: destination.dimensionalWeight,
  });

  // "Finalizar": save every destinatario as an EXEMPT guía, then collect ONE
  // shared declaración jurada for the whole lote on the resume screen.
  const onFinalize = async () => {
    if (!user) {
      alertService.error("El usuario parece que no esta loggeado.");
      return;
    }
    if (!buSource) {
      alertService.error("No se ha cargado la tienda origen del usuario.");
      return;
    }
    if (!shipper) {
      alertService.error("El remitente es obligatorio.");
      return;
    }

    // Auto-include a filled current form so the agent doesn't lose the last
    // destinatario by forgetting to click "Añadir destinatario".
    const currentHasData = !!consignee || !!buConsignee || pieces.length > 0;
    let finalDestinations = drafts;
    if (currentHasData) {
      const destination = buildCurrentDestination();
      if (!destination) return;
      finalDestinations = [...drafts, destination];
    }

    if (finalDestinations.length === 0) {
      alertService.error("Agrega al menos un destinatario.");
      return;
    }

    const saved: ShipmentInterface[] = [];
    for (const destination of finalDestinations) {
      const shipment = buildShipment(destination);
      const response = await saveShipment(
        shipment,
        user.user ?? undefined,
        applicationID
      );

      if (response.didError || !response.model) {
        alertService.error(
          `Hubo un error creando la guía para ${formatName(
            destination.consignee.accountFullName ?? ""
          )}`,
          response.errorMessage,
          { autoClose: false }
        );
        // Keep the guías already saved so they can still be covered by a waiver.
        setShipments(saved);
        if (saved.length > 0) {
          setPhase(Phase.RESUME);
          setOpenOriginWaiverModal(true);
        }
        return;
      }

      saved.push({
        ...shipment,
        shipmentNumber: response.model.shipmentNumber,
        id: response.model.shipmentHeaderID,
      });
    }

    setShipments(saved);
    setPhase(Phase.RESUME);
    setOpenOriginWaiverModal(true);
  };

  const handleDownloadDocuments = async () => {
    if (shipments.length === 0) {
      alertService.error(
        "Error al imprimir el documento",
        "No se encontraron los identificadores."
      );
      return;
    }

    alertService.info("Imprimiendo documentos...");

    // ONE merged PDF for the whole lote, not one download per guía: the lote
    // shares a single declaración jurada, so it has to print as a single
    // document. Same endpoint the manual-batch resume screen uses.
    const fileResult = await getAllDeclaredShipmentDocuments(
      shipments.map((shipment) => ({
        shipmentNumber: shipment.shipmentNumber ?? "",
        shipmentHeaderID: shipment.id,
      }))
    );

    if (!fileResult) {
      alertService.error("No se pudieron descargar los documentos del lote.");
      return;
    }

    const fileURL = URL.createObjectURL(fileResult.file);
    const a = document.createElement("a");
    a.href = fileURL;
    a.download = fileResult.fileName;
    a.click();
    URL.revokeObjectURL(fileURL);

    alertService.success("Documentos impresos satisfactoriamente.");
  };

  const handlePrintLabels = async () => {
    if (shipments.length === 0) {
      alertService.error(
        "Error al imprimir la etiqueta",
        "No se encontraron los identificadores."
      );
      return;
    }

    // Sequential awaited loop (ordered + errors surfaced).
    for (const shipment of shipments) {
      if (!shipment.shipmentNumber || !shipment.service) continue;

      alertService.info(
        "Guía: " + shipment.shipmentNumber,
        "Imprimiendo etiqueta..."
      );
      const printSucceed = await PrintShipmentLabel(
        +shipment.shipmentNumber,
        shipment.service ?? 0,
        shipment.buSource.code,
        user.user?.userLogin ?? "",
        buSource?.labelFormat ?? 0
      );

      if (!!printSucceed) {
        alertService.success(
          "Guía:" + shipment.shipmentNumber,
          "La etiqueta se imprimió correctamente."
        );
      }
    }
  };

  const handleFinish = async () => {
    // Already saved, or a save is in flight: a repeat click would duplicate the
    // waiver (and with it the fingerprint and photos in the PDF).
    if (savingWaiverRef.current || waiverSavedRef.current) return;

    const shipmentIDs = shipments
      .map((shipment) => shipment.id)
      .filter((id): id is string => !!id);

    if (shipmentIDs.length === 0) {
      alertService.error("No hay ninguna guía cargada");
      return;
    }
    if (!liabilityName || !liabilityId) {
      alertService.error("Parece que no se ha cargado la declaración jurada.");
      return;
    }

    // The upload is slow (one fingerprint + up to 5 photos per guía, all
    // sequential), so show the loader for the whole round trip — an inert
    // screen is exactly what makes agents click LISTO a second time.
    savingWaiverRef.current = true;
    loaderService.start();

    let liabilityWaiver;
    try {
      liabilityWaiver = await saveLiabilityWaver(
        liabilityFingerprint,
        liabilityAttachments,
        shipmentIDs,
        isPhysicalLiability,
        liabilityName,
        liabilityId,
        user.user?.userLogin ?? "",
        1,
        liabilityObservation
      );
    } finally {
      loaderService.stop();
    }

    if (!liabilityWaiver || liabilityWaiver.didError) {
      // Release the latch so the agent can retry a genuinely failed save.
      savingWaiverRef.current = false;
      alertService.error(
        liabilityWaiver?.errorMessage ??
          "No se pudo guardar la declaración jurada."
      );
      return;
    }

    // H1: the declaración jurada now covers every guía in the lote.
    waiverSavedRef.current = true;

    navigate(`/shipments/${shipments[0].shipmentNumber}`);
  };

  const filterBusinessUnit = (
    option: BusinessUnitInterface,
    search: string
  ) => {
    const tokens = search
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .split(" ");

    return tokens.every((token) => {
      return (
        token === "-" ||
        option.location.name.toLowerCase().includes(token) ||
        option.code?.toLowerCase().includes(token) ||
        option.location.address.toLowerCase().includes(token) ||
        !!option.location.postalCode?.toLowerCase().includes(token) ||
        !!option.location.reference?.toLowerCase().includes(token)
      );
    });
  };

  useEffect(() => {
    const serviceID = ShipmentService.INTERNAL_MAIL;
    if (serviceID === undefined) {
      return;
    }

    const afunction = async () => {
      const service = await getService(serviceID);

      if (!service.didError && service.model) {
        setService(service.model);
      }
    };

    afunction();
  }, []);

  useEffect(() => {
    if (pieces.length === 0) {
      setPhysicalWeight(0);
      setDimensionalWeight(0);
      setChargedlWeight(0);
      return;
    }

    const getShipmentWeight = async () => {
      const weights = await getWeights(
        236,
        10,
        shipper?.id,
        undefined,
        ShipmentService.INTERNAL_MAIL,
        PaymentMode.EXEMPT,
        pieces
      );

      if (!weights || weights.didError || !weights.model) return;

      setPhysicalWeight(weights.model.physicalWeight);
      setDimensionalWeight(weights.model.dimensionalWeight);
      setChargedlWeight(weights.model.chargedWeight);
    };

    getShipmentWeight();
  }, [pieces, shipper?.id]);

  useEffect(() => {
    const afunction = async () => {
      const businessUnits = await getBusinessUnits(true);
      if (businessUnits !== null) {
        setBusinessUnits(
          businessUnits.filter(
            (b) =>
              (b.type === BusinessType.VIRTUAL_STORE_FOR_INTERNAL_USE ||
                b.type === BusinessType.INTERNAL ||
                b.type === BusinessType.EXTERNAL) &&
              !b.isLocked
          )
        );
      }
    };

    afunction();
  }, []);

  return (
    <main className="lg:pl-72 pb-32">
      {/* Header */}
      <div className="py-8 sm:px-6 lg:px-8 bg-white relative flex items-center justify-between h-32">
        <header className="ml-4 text-2xl font-bold text-gray-700 ">
          Correo Interno
        </header>
      </div>

      <div>
        <HorizontalPadding paddingTop>
          <div
            className={classNames(
              "flex flex-col",
              phase !== Phase.FORM && "hidden"
            )}
          >
            <div className="flex justify-end">
              <LinkText text="Limpiar todo" onClick={onClean} />
            </div>

            {/* Shipper and Consignee description */}
            <div className="flex flex-col flex-1 px-6 py-4 bg-white rounded-md border">
              <div className="relative flex items-center">
                <div className="gap-8 grid w-full grid-cols-2">
                  {/* Shipper */}
                  <div className="flex flex-1 flex-col">
                    <div className="flex flex-col xl:flex-row xl:gap-4 xl:items-center">
                      <p className="font-semibold">Remitente:</p>
                      <div className="flex items-center truncate">
                        {formatName(shipper?.accountFullName ?? "")}

                        <CheckBadgeIcon
                          title="Cliente Crédito Corporativo"
                          data-te-toggle="tooltip"
                          className={classNames(
                            "ml-2 inline h-5 w-5 text-indigo-600",
                            !isShipperEnterprise && "hidden"
                          )}
                        />
                      </div>
                    </div>

                    <ClientDetails client={shipper} />
                  </div>

                  {/* Consignee */}
                  <div className="flex flex-1 flex-col">
                    <div className="flex flex-col xl:flex-row xl:gap-4 xl:items-center">
                      <p className="font-semibold">Destinatario:</p>
                      <div className="flex items-center truncate">
                        {formatName(consignee?.accountFullName ?? "")}

                        <CheckBadgeIcon
                          title="Cliente Crédito Corporativo"
                          data-te-toggle="tooltip"
                          className={classNames(
                            "ml-2 inline h-5 w-5 text-indigo-600",
                            !isConsigneeEnterprise && "hidden"
                          )}
                        />
                      </div>
                    </div>

                    <ClientDetails client={consignee} />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex flex-1 flex-col pt-4">
                  {/* buSource */}
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-center gap-4">
                      <p className="font-semibold mr-2">Origen:</p>
                      <div className="flex flex-col">
                        {!!buSource?.code ? (
                          <p className="text-sm">
                            {buSource?.code} - {buSource?.location.name}
                          </p>
                        ) : (
                          <p className="text-sm">{buSource?.location.name}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex flex-1 flex-col mt-2">
                    <div className="flex items-start gap-4">
                      <p className="font-semibold">Destino:</p>
                      <div className="flex flex-col">
                        <p
                          className={classNames(
                            "text-sm",
                            !buConsignee && "hidden"
                          )}
                        >
                          {buConsignee?.code} - {buConsignee?.location?.name}
                        </p>

                        <p className="text-xs">
                          {buConsignee?.location.name}
                          {" - "}
                          {buConsignee?.location.address}{" "}
                          {!!buConsignee?.location.postalCode
                            ? `(código postal: ${buConsignee?.location.postalCode})`
                            : ""}
                        </p>
                        <p className="text-xs">
                          {buConsignee?.location.reference}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="flex flex-1 flex-col bg-white rounded-lg border px-8 py-6 gap-6 mt-8">
              <div className="flex flex-1 2xl:flex-row flex-col gap-4">
                {/* Search shipper */}
                <div className="relative flex flex-1 flex-col">
                  <AccountSearch
                    title="Remitente"
                    selected={shipper}
                    ignoredAccounts={[
                      {
                        abreviationName: "J-",
                        identificationNumber: "306842675",
                      },
                    ]}
                    onSelectClient={handleShipperChange}
                    openCreationModal={() => setOpenShipperCreateModal(true)}
                  />
                  {drafts.length > 0 && (
                    <p className="text-xs text-gray-500 leading-4 mt-1">
                      Mismo remitente para todo el lote.
                    </p>
                  )}
                </div>

                {/* Search consignee */}
                <div className="flex flex-1 flex-col">
                  <AccountSearch
                    title="Destinatario"
                    selected={consignee}
                    ignoredAccounts={[
                      {
                        abreviationName: "J-",
                        identificationNumber: "306842675",
                      },
                    ]}
                    onSelectClient={handleConsigneeChange}
                    openCreationModal={() => setOpenConsigneeCreateModal(true)}
                  />
                </div>
              </div>

              <div className="flex flex-1 2xl:flex-row flex-col gap-4">
                <div className="flex flex-1 flex-col gap-2">
                  <label className="block text-m font-medium text-gray-900 truncate">
                    Destino
                  </label>

                  <div className="relative">
                    <FormSearch
                      value={businessUnitSearch}
                      options={businessUnitOptions}
                      onChange={(e) => setBusinessUnitSearch(e.target.value)}
                      placeholder="Buscar tienda..."
                      filter={filterBusinessUnit}
                      onSelectOption={(option) => {
                        setBUConsignee(option);
                        setBusinessUnitSearch(
                          `${option.code} - ${option.location.name}`
                        );
                      }}
                      onChangeFocus={(focus) => {
                        if (!focus && buConsignee)
                          setBusinessUnitSearch(
                            `${buConsignee.code} - ${buConsignee.location.name}`
                          );
                      }}
                      RenderOption={({ option }) => (
                        <BusinessUnitOption {...option} />
                      )}
                    />
                  </div>
                </div>

                <div className="flex flex-1 flex-col">
                  <FormTextInput
                    value={reference}
                    onChange={(event) => setReference(event.target.value)}
                    name="reference"
                    label="Dirección de entrega"
                  />
                </div>
              </div>

              <hr className="border-gray-300 my-6" />

              <div className="flex flex-1 flex-col gap-2">
                <ShipmentPiecesDetails
                  id=""
                  typeDisabled
                  applyMinValue
                  pieces={pieces}
                  cleaned={cleaned}
                  setCleaned={setCleaned}
                  setPieces={setPieces}
                  maxSumWeightWarn={service?.maxWeight}
                  service={ShipmentService.INTERNAL_MAIL}
                  pieceCategories={pieceCategories}
                  paymentMode={PaymentMode.EXEMPT}
                  setServiceType={() => {}}
                  serviceOptions={[]}
                  chargedWeight={chargedWeight}
                  physicalWeight={physicalWeight}
                  dimensionalWeight={dimensionalWeight}
                />
              </div>

              <hr className="border-gray-300 my-6" />

              {/* Observations */}
              <div className="flex flex-1 flex-col">
                <FormTextAreaInput
                  rows={4}
                  maxLength={192}
                  value={observations}
                  onChange={(event) => setObservations(event.target.value)}
                  name="observations"
                  label="Observaciones"
                  className="resize-none"
                />
              </div>
            </div>

            {/* Destinatarios added to the lote so far */}
            {drafts.length > 0 && (
              <div className="flex flex-1 flex-col bg-white rounded-lg border px-8 py-6 gap-3 mt-8">
                <p className="font-semibold">
                  Destinatarios del lote ({drafts.length})
                </p>
                <p className="text-xs text-gray-500 leading-4">
                  Todas las guías de este lote comparten una sola Declaración
                  Jurada.
                </p>
                <ul className="flex flex-col gap-2">
                  {drafts.map((destination, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between gap-4 text-sm border-b last:border-b-0 pb-2"
                    >
                      <span className="truncate">
                        {index + 1}.{" "}
                        {formatName(
                          destination.consignee.accountFullName ?? ""
                        )}{" "}
                        — {destination.buConsignee.code}{" "}
                        {destination.buConsignee.location?.name} (
                        {destination.pieces.length} pieza
                        {destination.pieces.length === 1 ? "" : "s"})
                      </span>
                      <LinkText
                        text="Quitar"
                        onClick={() => removeDestination(index)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-1 gap-4 justify-end mt-8">
              <SecondaryButton
                disabled={loading}
                onClick={() => addDestination()}
                className="!px-8"
              >
                Añadir destinatario
              </SecondaryButton>

              <PrimaryButton
                disabled={loading}
                onClick={async () => {
                  loaderService.start();
                  await onFinalize();
                  loaderService.stop();
                }}
                className="!px-8"
              >
                {drafts.length > 0
                  ? `Finalizar (${drafts.length})`
                  : "Finalizar"}
              </PrimaryButton>
            </div>
          </div>

          <div
            className={classNames(
              "flex flex-col",
              phase !== Phase.RESUME && "hidden"
            )}
          >
            <div className="flex flex-1 flex-col bg-white rounded-lg border px-8 pb-6 pt-4 gap-8">
              <table className="table-auto w-full">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold text-xs">
                      GUÍA
                    </th>

                    <th className="text-left px-4 py-2 font-semibold text-xs">
                      DESTINATARIO
                    </th>

                    <th className="text-left px-4 py-2 font-semibold text-xs">
                      DESTINO
                    </th>

                    <th className="text-right px-4 py-2 font-semibold text-xs">
                      MONTO
                    </th>

                    <th className="text-left px-4 py-2 font-bold text-gray-700 text-xs w-5" />
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((shipment, index) => (
                    <ShipmentItem
                      key={index}
                      index={index}
                      shipment={shipment}
                    />
                  ))}
                </tbody>
              </table>

              <div>
                <p
                  className={classNames(
                    "text-red-500",
                    !dataMessage && "hidden"
                  )}
                >
                  {dataMessage}
                </p>

                <p
                  className={classNames(
                    !liabilityFingerprint &&
                      !isPhysicalLiability &&
                      "text-red-500"
                  )}
                >
                  {statusMessage}
                </p>
                <p className={classNames(!alertMessage && "hidden")}>
                  {alertMessage}
                </p>
              </div>

              <div className="flex flex-1 gap-4 justify-start items-center">
                <SecondaryButton
                  className="w-48"
                  disabled={loading}
                  onClick={async () => {
                    loaderService.start();
                    await handlePrintLabels();
                    loaderService.stop();
                  }}
                >
                  Imprimir Etiquetas
                </SecondaryButton>

                <SecondaryButton
                  className="w-48"
                  disabled={loading}
                  onClick={async () => {
                    loaderService.start();
                    await handleDownloadDocuments();
                    loaderService.stop();
                  }}
                >
                  Imprimir Documentos
                </SecondaryButton>

                <SecondaryButton
                  className="w-52"
                  onClick={() => setOpenOriginWaiverModal(true)}
                >
                  Cargar Declaración Jurada
                </SecondaryButton>
              </div>
            </div>

            <div className="flex flex-1 gap-4 justify-end items-center mt-8">
              <PrimaryButton
                className="w-32"
                disabled={
                  loading ||
                  (!isPhysicalLiability && !liabilityFingerprint) ||
                  !liabilityName ||
                  !liabilityId
                }
                onClick={handleFinish}
              >
                <ClipboardDocumentCheckIcon
                  aria-hidden="true"
                  className="h-5 w-5 flex-none text-white-400 mr-2"
                />
                LISTO
              </PrimaryButton>
            </div>
          </div>
        </HorizontalPadding>
      </div>

      <CreateClientModal
        openModal={openShipperCreateModal}
        setOpenModal={setOpenShipperCreateModal}
        setSelectedItem={handleShipperChange}
      />

      <CreateClientModal
        openModal={openConsigneeCreateModal}
        setOpenModal={setOpenConsigneeCreateModal}
        setSelectedItem={handleConsigneeChange}
      />

      {shipper && (
        <UpdateClientModal
          selectedItem={shipper}
          openModal={openShipperEditModal}
          setSelectedItem={handleShipperChange}
          setOpenModal={setOpenShipperEditModal}
        />
      )}

      {consignee && (
        <UpdateClientModal
          selectedItem={consignee}
          openModal={openConsigneeEditModal}
          setSelectedItem={handleConsigneeChange}
          setOpenModal={setOpenConsigneeEditModal}
        />
      )}

      {shipments.length > 0 && (
        <OriginModal
          openModal={openOriginWaiverModal}
          setOpenModal={setOpenOriginWaiverModal}
          shipmentNumbers={shipments
            .map((shipment) => shipment.shipmentNumber)
            .filter((number): number is string => number !== undefined)}
          shipments={shipments}
          shipper={shipments[0].shipper}
          setExtFingerprint={setLiabilityFingerprint}
          setExtAttachments={setLiabilityAttachments}
          setExtName={setLiabilityName}
          setExtId={setLiabilityId}
          setExtObservation={setLiabilityObservation}
          setIsPhysical={setIsPhysicalLiability}
        />
      )}
    </main>
  );
};

export default InternalMail;
